import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import StatusBadge from "@/components/StatusBadge";
import { usd, shortDate } from "@/lib/format";
import { FileText, AlertTriangle, Clock, Copy } from "lucide-react";
import { toast } from "sonner";

export default function Detention() {
  const [events, setEvents] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [waiver, setWaiver] = useState(null);

  useEffect(() => {
    api.get("/detention/events").then(r => setEvents(r.data));
    api.get("/shipments").then(r => setShipments(r.data));
  }, []);

  const totalExposure = events.reduce((acc, e) => acc + (e.exposure_usd || 0), 0);
  const critical = events.filter(e => e.status === "CRITICAL").length;
  const overdue = events.filter(e => e.status === "OVERDUE").length;

  const generateWaiver = async (id) => {
    const r = await api.post(`/detention/waiver/${id}`);
    setWaiver(r.data);
  };

  // High-risk shipments cross-ref
  const highRiskShipments = shipments.filter(s => s.detention_risk === "HIGH" && s.status !== "DELIVERED");

  return (
    <div className="space-y-6 animate-slide-up" data-testid="detention-page">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight">Detention &amp; Demurrage</h1>
        <p className="text-sm text-zinc-500 mt-1">Avoid charges before they happen. Generate waiver and dispute packs in seconds.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-200 border border-zinc-200">
        <div className="bg-white p-5 border-l-[3px] border-l-[#DC2626]">
          <div className="data-label">Total Exposure</div>
          <div className="metric text-3xl mt-2">{usd(totalExposure)}</div>
          <div className="text-xs text-zinc-500 mt-2">open D&amp;D liability</div>
        </div>
        <div className="bg-white p-5 border-l-[3px] border-l-[#F59E0B]">
          <div className="data-label">Critical (≤2d to LFD)</div>
          <div className="metric text-3xl mt-2">{critical}</div>
          <div className="text-xs text-zinc-500 mt-2">action required</div>
        </div>
        <div className="bg-white p-5 border-l-[3px] border-l-[#DC2626]">
          <div className="data-label">Overdue</div>
          <div className="metric text-3xl mt-2">{overdue}</div>
          <div className="text-xs text-zinc-500 mt-2">accruing charges</div>
        </div>
        <div className="bg-white p-5">
          <div className="data-label">High-Risk Shipments</div>
          <div className="metric text-3xl mt-2">{highRiskShipments.length}</div>
          <div className="text-xs text-zinc-500 mt-2">in transit</div>
        </div>
      </div>

      {/* Detention events */}
      <div className="bg-white border border-zinc-200">
        <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between">
          <div>
            <h3 className="font-heading text-lg font-bold tracking-tight">Free-Time Countdown</h3>
            <p className="text-xs text-zinc-500 mt-0.5">Containers ranked by days to Last Free Day.</p>
          </div>
        </div>
        <div className="overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr className="text-left">
                {["Container", "Shipment", "Carrier", "Last Free Day", "Days to LFD", "Responsible", "Exposure", "Status", ""].map(h => <th key={h} className="px-4 py-2.5 data-label">{h}</th>)}
              </tr>
            </thead>
            <tbody data-testid="detention-tbody">
              {events.sort((a,b) => a.days_to_lfd - b.days_to_lfd).map(e => (
                <tr key={e.id} className="border-b border-zinc-100 hover:bg-zinc-50/60">
                  <td className="px-4 py-2.5 metric font-medium">{e.container}</td>
                  <td className="px-4 py-2.5 metric text-zinc-700">{e.shipment_id}</td>
                  <td className="px-4 py-2.5">{e.carrier}</td>
                  <td className="px-4 py-2.5 metric">{shortDate(e.last_free_day)}</td>
                  <td className="px-4 py-2.5 metric font-semibold">
                    <span className={e.days_to_lfd < 0 ? "text-[#991B1B]" : e.days_to_lfd <= 2 ? "text-[#B45309]" : "text-zinc-700"}>
                      {e.days_to_lfd}d
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-zinc-600">{e.responsible_party}</td>
                  <td className="px-4 py-2.5 metric">{e.exposure_usd > 0 ? <span className="text-[#991B1B]">{usd(e.exposure_usd)}</span> : "—"}</td>
                  <td className="px-4 py-2.5"><StatusBadge value={e.status === "CRITICAL" ? "CRITICAL_DETENTION" : e.status} label={e.status} /></td>
                  <td className="px-4 py-2.5">
                    <Button
                      data-testid={`waiver-btn-${e.id}`}
                      size="sm" variant="outline"
                      className="rounded-none border-[#002FA7] text-[#002FA7] hover:bg-[#E6EBF6] h-8"
                      onClick={() => generateWaiver(e.id)}
                    ><FileText className="w-3 h-3 mr-1.5" />Waiver</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!waiver} onOpenChange={(o) => !o && setWaiver(null)}>
        <DialogContent className="rounded-none max-w-2xl" data-testid="waiver-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Detention Waiver Request</DialogTitle>
            <DialogDescription className="text-xs text-zinc-500">Ready-to-send template. Edit if needed, then copy and email to the carrier detention desk.</DialogDescription>
          </DialogHeader>
          {waiver && (
            <>
              <Textarea value={waiver.waiver_text} readOnly className="rounded-none min-h-[300px] font-mono text-xs" data-testid="waiver-text" />
              <div className="flex justify-end gap-2">
                <Button
                  data-testid="copy-waiver"
                  variant="outline"
                  className="rounded-none"
                  onClick={() => {
                    navigator.clipboard.writeText(waiver.waiver_text);
                    toast.success("Waiver copied to clipboard");
                  }}
                ><Copy className="w-4 h-4 mr-2" />Copy</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
